/**
 * Zustand-based AuthProvider
 *
 * Centralized authentication state management using Zustand store.
 * This provides the same API as the old AuthProvider but with better performance
 * and universal access patterns.
 *
 * Features:
 * - Zustand store integration
 * - Automatic localStorage persistence
 * - OAuth flow management
 * - Universal access (React + non-React)
 * - Type-safe authentication context
 */

import { createContext, type ReactNode, useContext, useEffect } from "react";
import { useAuthProvider } from "@/stores/auth-hooks";
import { getAuthState, initializeAuthStore } from "@/stores/auth-store";
import type { AuthContextType } from "@/types/auth.types";
import { logApp } from "@/utils/logApp";

/**
 * Auth Context - maintains same interface as original
 */
const AuthContext = createContext<AuthContextType | null>(null);

/**
 * useAuth hook - maintains same API as original
 */
export const useAuth = (): AuthContextType => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
};

/**
 * AuthProvider Props
 */
export interface AuthProviderProps {
	children: ReactNode;
}

/**
 * Zustand-based AuthProvider
 *
 * This replaces the old AuthProvider with Zustand store integration
 * while maintaining the same public API for backward compatibility.
 */
export function AuthProvider({ children }: AuthProviderProps) {
	// Get auth state and actions from Zustand store
	const authState = useAuthProvider();

	// Initialize the auth store on mount
	useEffect(() => {
		let isMounted = true;

		const initialize = async () => {
			try {
				// console.log("🔄 Zustand AuthProvider: Initializing auth store...");
				await initializeAuthStore();

				if (isMounted) {
					// console.log(
					// 	"✅ Zustand AuthProvider: Auth store initialized successfully",
					// );
				}
			} catch (error) {
				if (isMounted) {
					console.error(
						"❌ Zustand AuthProvider: Failed to initialize auth store:",
						error,
					);
					logApp({
						level: "error",
						message: `Zustand AuthProvider: Failed to initialize auth store: ${error}`,
					});
				}
			}
		};

		initialize();

		return () => {
			isMounted = false;
		};
	}, []);

	// Log auth state changes for debugging
	// useEffect(() => {
	// 	console.log("🔄 Zustand AuthProvider: Auth state changed:", {
	// 		isAuthenticated: authState.isAuthenticated,
	// 		isLoading: authState.isLoading,
	// 		isInitializing: authState.isInitializing,
	// 		hasUser: !!authState.user,
	// 		hasError: !!authState.error,
	// 	});
	// }, [
	// 	authState.isAuthenticated,
	// 	authState.isLoading,
	// 	authState.isInitializing,
	// 	authState.user,
	// 	authState.error,
	// ]);

	// Create context value with same interface as original AuthProvider
	const contextValue: AuthContextType = {
		// State
		isAuthenticated: authState.isAuthenticated,
		isLoading: authState.isLoading,
		isInitializing: authState.isInitializing,
		isProcessingCallback: authState.isProcessingCallback,
		user: authState.user,
		error: authState.error,
		lastAuthCheck: getAuthState().lastAuthCheck,

		// Actions
		login: authState.login,
		logout: authState.logout,
		handleOAuthReturn: authState.handleOAuthReturn,
		checkAuthenticationStatus: authState.checkAuthenticationStatus,
		setCallbackProcessing: authState.setCallbackProcessing,
		hasPermission: authState.hasPermission,
		hasRole: authState.hasRole,
		hasAnyPermission: authState.hasAnyPermission,
		hasAllPermissions: authState.hasAllPermissions,
		switchOrganization: authState.switchOrganization,
		getUserOrganizations: authState.getUserOrganizations,
		can: authState.can,
		cannot: authState.cannot,
	};

	return (
		<AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
	);
}

/**
 * ✅ SYNCHRONOUS auth state reader for immediate access
 * Now uses Zustand store instead of direct localStorage access
 */
export function getAuthStateSync(): {
	isAuthenticated: boolean;
	user: import("@/types/auth.types").User | null;
} {
	const state = getAuthState();
	return {
		isAuthenticated: state.isAuthenticated,
		user: state.user,
	};
}

/**
 * HOC for components that need auth context
 */
export function withAuth<P extends object>(
	Component: React.ComponentType<P>,
): React.ComponentType<P> {
	return function AuthenticatedComponent(props: P) {
		const auth = useAuth();
		return <Component {...props} auth={auth} />;
	};
}

/**
 * Auth guard component for conditional rendering
 */
interface AuthGuardProps {
	children: ReactNode;
	fallback?: ReactNode;
	requireAuth?: boolean;
	requiredPermissions?: string[];
	requiredRoles?: string[];
	requireAll?: boolean; // true = all permissions/roles, false = any
}

export function AuthGuard({
	children,
	fallback = null,
	requireAuth = true,
	requiredPermissions = [],
	requiredRoles = [],
	requireAll = false,
}: AuthGuardProps) {
	const auth = useAuth();

	// Check authentication
	if (requireAuth && !auth.isAuthenticated) {
		return <>{fallback}</>;
	}

	// Check permissions
	if (requiredPermissions.length > 0) {
		const hasPermissions = requireAll
			? auth.hasAllPermissions(requiredPermissions)
			: auth.hasAnyPermission(requiredPermissions);

		if (!hasPermissions) {
			return <>{fallback}</>;
		}
	}

	// Check roles
	if (requiredRoles.length > 0) {
		const hasRoles = requireAll
			? requiredRoles.every((role) => auth.hasRole(role))
			: requiredRoles.some((role) => auth.hasRole(role));

		if (!hasRoles) {
			return <>{fallback}</>;
		}
	}

	return <>{children}</>;
}

/**
 * Loading wrapper component
 */
interface AuthLoadingProps {
	children: ReactNode;
	fallback?: ReactNode;
}

export function AuthLoading({ children, fallback }: AuthLoadingProps) {
	const auth = useAuth();

	if (auth.isLoading || auth.isInitializing) {
		return <>{fallback}</>;
	}

	return <>{children}</>;
}

/**
 * Export the context for advanced usage
 */
export { AuthContext };
